import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import LightGestion from 'components/headers/lightGestion';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

// Price adjustment page inspired by provided screenshot
const EditPrice = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [marginBase, setMarginBase] = useState('retail_price'); // 'retail_price' | 'purchase_price' | 'wholesale_price' | 'carton_price'
  const [changes, setChanges] = useState({}); // id -> { purchase_price, retail_price, wholesale_price, carton_price }
  const filterMode = 'all';
  const { t } = useTranslation();

  const fetchProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await axios.get('https://plateformewmg.cloud/api/Gestion/products', { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data || []);
    } catch (e) {
      console.error(e);
      Swal.fire(t('price.error', 'Erreur'), t('price.fetchFailed', 'Impossible de récupérer les produits'));
    }
  }, [t]);
console.log('products',products);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const getFirst = useCallback((obj, keys) => {
    if (!obj) return undefined;
    for (const k of keys) if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    return undefined;
  }, []);

  const num = useCallback(v => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const purchaseOf = useCallback(p => num(getFirst(p, ['purchase_price', 'prix_achat', 'PrixAchat', 'purchasePrice'])), [getFirst, num]);
  const retailOf = useCallback(p => num(getFirst(p, ['retail_price', 'prix_detail', 'PrixDetail', 'retailPrice'])), [getFirst, num]);
  const wholesaleOf = useCallback(p => num(getFirst(p, ['wholesale_price', 'prix_gros', 'PrixGros'])), [getFirst, num]);
  const cartonOf = useCallback(p => num(getFirst(p, ['carton_price', 'prix_carton', 'PrixCarton'])), [getFirst, num]);

  const visible = useMemo(() => products.filter(p => {
    const nameObj = getFirst(p, ['Name', 'name']);
    const name = (nameObj?.fr || nameObj?.en || nameObj || '').toString().toLowerCase();
    if (!name.includes(search.toLowerCase())) return false;
    if (filterMode === 'all') return true;
    if (filterMode === 'modified') return Boolean(changes[p.ProductID || p.ID || p.product_id]);
    return true;
  }), [products, search, filterMode, getFirst, changes]);

  const setField = (id, field, value) => {
    setChanges(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const resetChanges = () => setChanges({});

  const applyBulk = (mode, amount, isPercent) => {
    const visibleIds = visible.map(p => p.ProductID || p.ID || p.product_id).filter(Boolean);
  if (visibleIds.length === 0) return Swal.fire(t('price.info', 'Info'), t('price.noVisible', 'Aucun produit visible'));
    const amt = Number(amount || 0);
    setChanges(prev => {
      const copy = { ...prev };
      visibleIds.forEach(id => {
        const prod = products.find(p => (p.ProductID || p.ID || p.product_id) === id);
        if (!prod) return;
        const baseVal = (marginBase === 'purchase_price') ? purchaseOf(prod) : (marginBase === 'wholesale_price') ? wholesaleOf(prod) : (marginBase === 'carton_price') ? cartonOf(prod) : retailOf(prod);
        let newVal = baseVal;
        if (isPercent) {
          newVal = mode === 'add' ? baseVal * (1 + amt / 100) : baseVal * (1 - amt / 100);
        } else {
          newVal = mode === 'add' ? baseVal + amt : baseVal - amt;
        }
        // apply to retail_price by default
        copy[id] = { ...(copy[id] || {}), retail_price: String(Number(newVal).toFixed(2)) };
      });
      return copy;
    });
  };

  const computeMargin = (p) => {
    const purchase = purchaseOf(p);
    const retail = Number(changes[p.ProductID || p.ID || p.product_id]?.retail_price ?? retailOf(p));
    if (retail === 0) return 0;
    return ((retail - purchase) / retail) * 100;
  };

  const save = async () => {
    const items = Object.keys(changes).map(id => {
      const c = changes[id];
      return {
        product_id: Number(id),
        purchase_price: c.purchase_price !== undefined ? Number(c.purchase_price) : undefined,
        retail_price: c.retail_price !== undefined ? Number(c.retail_price) : undefined,
        wholesale_price: c.wholesale_price !== undefined ? Number(c.wholesale_price) : undefined,
        carton_price: c.carton_price !== undefined ? Number(c.carton_price) : undefined,
      };
    }).map(x => Object.fromEntries(Object.entries(x).filter(([k,v]) => v !== undefined)));
    console.log("Payload to backend:", items);

    if (items.length === 0) return Swal.fire(t('price.info', 'Info'), t('price.noChanges', 'Aucune modification à sauvegarder'));
    try {
      const token = localStorage.getItem('authToken');
      await axios.post('https://plateformewmg.cloud/api/Gestion/products/update-prices', { items }, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire(t('price.success', 'Succès'), t('price.updated', 'Prix mis à jour'), 'success');
      setChanges({});
      fetchProducts();
    } catch (e) {
      console.error(e);
      Swal.fire(t('price.error', 'Erreur'), JSON.stringify(e?.response?.data || e.message));
    }
  };

  return (
    <AnimationRevealPage>
      <LightGestion />

      <div className="container  app-content" style={{ maxWidth: 1100 ,marginTop:"-60px",marginLeft:"-20px"}}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 style={{ fontWeight: 900, fontSize: '30px', marginBottom: 6 }}>{t('price.title', 'Ajustement des Prix')}</h2>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', color: '#6b7280' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#2b6cb0', display: 'inline-block' }} /> {t('price.total', 'Total')}: {products.length}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#ff7a00' }}>{t('price.modified', 'Modifiés')}: {Object.keys(changes).length}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button variant="outline-secondary" onClick={resetChanges} style={{ minWidth: 160 }}>{t('price.reset', 'Réinitialiser')}</Button>
            <Button variant="success" onClick={save} style={{ minWidth: 220, fontWeight: 700 }}>{t('price.save', 'Enregistrer les Modifications')}</Button>
          </div>
        </div>

        <div style={{ marginTop: 18, background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #eef2f7' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <InputGroup style={{ flex: 1 }}>
              <InputGroup.Text>🔍</InputGroup.Text>
              <FormControl placeholder={t('price.searchPlaceholder', 'Rechercher un produit...')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </InputGroup>

            <div style={{ display: 'flex', gap: 8 }}>
              <select value={marginBase} onChange={(e) => setMarginBase(e.target.value)} style={{ padding: '8px 10px', borderRadius: 6 }}>
                <option value="retail_price">{t('price.baseOptions.retail_price', t('Prix_Detail', 'Prix Détail'))}</option>
                <option value="purchase_price">{t('price.baseOptions.purchase_price', t('Prix_achat', "Prix d'achat"))}</option>
                <option value="wholesale_price">{t('price.baseOptions.wholesale_price', t('Prix_Gros', 'Prix Gros'))}</option>
                <option value="carton_price">{t('price.baseOptions.carton_price', t('Prix_Carton', 'Prix Carton'))}</option>
              </select>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select id="bulk-mode" defaultValue="add">
                  <option value="add">{t('price.bulk.add', 'Ajouter')}</option>
                  <option value="sub">{t('price.bulk.sub', 'Soustraire')}</option>
                </select>
                <select id="bulk-kind" defaultValue="percent">
                  <option value="percent">{t('price.bulk.percent', '%')}</option>
                  <option value="absolute">{t('price.bulk.absolute', { currency: t('stock.currency', 'MAD') })}</option>
                </select>
                <input id="bulk-amount" type="number" defaultValue={0} style={{ width: 100, padding: '8px 10px', borderRadius: 6 }} />
                <Button variant="secondary" onClick={() => {
                  const mode = document.getElementById('bulk-mode').value;
                  const kind = document.getElementById('bulk-kind').value;
                  const amt = Number(document.getElementById('bulk-amount').value || 0);
                  applyBulk(mode, amt, kind === 'percent');
                }}>{t('price.bulk.apply', 'Appliquer')}</Button>
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f3f4f6' }} />

          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 160px 160px 160px 160px 120px', gap: 12, padding: '12px', alignItems: 'center', color: '#6b7280', fontWeight: 700 }}>
              <div />
              <div>{t('price.headers.product', 'PRODUIT')}</div>
              <div style={{ textAlign: 'center' }}>{t('price.headers.purchase', "PRIX D'ACHAT")}</div>
              <div style={{ textAlign: 'center' }}>{t('price.headers.retail', 'PRIX DÉTAIL')}</div>
              <div style={{ textAlign: 'center' }}>{t('price.headers.wholesale', 'PRIX GROS')}</div>
              <div style={{ textAlign: 'center' }}>{t('price.headers.carton', 'PRIX CARTON')}</div>
              <div style={{ textAlign: 'center' }}>{t('price.headers.margin', 'MARGE')}</div>
            </div>

            <div style={{ maxHeight: 520, overflow: 'auto' }}>
              {visible.map((p, idx) => {
                const id = p.ProductID || p.id || p.product_id || p.ID;
                // prefer edited values from `changes` so inputs stay editable
                const purchase = changes[id]?.purchase_price !== undefined ? changes[id].purchase_price : String(purchaseOf(p));
                const retail = changes[id]?.retail_price !== undefined ? changes[id].retail_price : String(retailOf(p));
                const wholesale = changes[id]?.wholesale_price !== undefined ? changes[id].wholesale_price : String(wholesaleOf(p));
                const carton = changes[id]?.carton_price !== undefined ? changes[id].carton_price : String(cartonOf(p));
                const margin = computeMargin(p);
                const changed = !!changes[id];

                return (
                  <div key={id} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 160px 160px 160px 160px 120px', gap: 12, padding: 12, alignItems: 'center', background: changed ? '#fff7e6' : 'transparent', borderLeft: changed ? '4px solid #f59e0b' : '4px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#6b8cff', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(idx + 1)}</div>
                    </div>

                    <div style={{ minWidth: 0 }}>
<div
  style={{
    fontWeight: 700,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }}
>
  {p?.Name?.fr || p?.Name?.en || p?.Name?.ar || p?.name || p?.Name || '—'}
</div>

                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <input type="number" value={purchase} onChange={(e) => setField(id, 'purchase_price', e.target.value)} style={{ width: '80px', padding: '10px 12px', borderRadius: 12, border: '1px solid #e6e6e9', textAlign: 'center' ,marginLeft:'100px'}} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <input type="number" value={retail} onChange={(e) => setField(id, 'retail_price', e.target.value)} style={{ width: '80px', padding: '10px 12px', borderRadius: 12, border: '1px solid #e6e6e9', textAlign: 'center',marginLeft:'100px' }} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <input type="number" value={wholesale} onChange={(e) => setField(id, 'wholesale_price', e.target.value)} style={{ width: '80px', padding: '10px 12px', borderRadius: 12, border: '1px solid #e6e6e9', textAlign: 'center' ,marginLeft:'100px'}} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                      <input type="number" value={carton} onChange={(e) => setField(id, 'carton_price', e.target.value)} style={{ width: '80px', padding: '10px 12px', borderRadius: 12, border: '1px solid #e6e6e9', textAlign: 'center' ,marginLeft:'100px'}} />
                    </div>

                    <div style={{ textAlign: 'center', fontWeight: 700 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 22, background: margin < 10 ? '#fecaca' : (margin < 25 ? '#fef3c7' : '#e6fff0'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#111', fontWeight: 700,marginLeft:'50px' }}>{Math.round(margin * 10) / 10}%</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </AnimationRevealPage>
  );
};

export default EditPrice;
